class NWPurchaseWidget {
  constructor(root) {
    this.root = root;
    this.variants = this.parseVariants();
    this.texts = this.parseTexts();
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.shippingBlock = root.querySelector('.nw-purchase-widget-block--shipping');
    this.shippingText = root.querySelector('[data-shipping-text]');
    this.shippingProgressFill = root.querySelector('[data-shipping-progress-fill]');
    this.supplyFrequencyContainer = root.querySelector('[data-supply-frequency]');
    this.selectedRollerEl = root.querySelector('[data-selected-roller]');
    this.supplyBagsEl = root.querySelector('[data-supply-bags]');
    this.supplyWashesEl = root.querySelector('[data-supply-washes]');
    this.supplyCostEl = root.querySelector('[data-supply-cost]');
    this.quantityInputs = root.querySelectorAll('input[name="nw_quantity"]');
    this.rollerInputs = root.querySelectorAll('input[name="nw_roller"]');
    this.subscriptionToggle = root.querySelector('#nw-subscription-toggle');
    this.unlockIndex = parseInt(root.dataset.shippingUnlockIndex, 10);
    this.defaultQuantityIndex = parseInt(root.dataset.defaultQuantityIndex, 10);
    this.defaultRollerIndex = parseInt(root.dataset.defaultRollerIndex, 10);
    this.isSyncing = false;

    this.initProductForm();

    this.init();
  }

  initProductForm() {
    this.productInfoWrapper = this.root.closest('.product__info-wrapper');
    if (!this.productInfoWrapper) return;

    this.productForm =
      this.productInfoWrapper.querySelector('form[action="/cart/add"]') ||
      this.productInfoWrapper.querySelector('form[data-type="add-to-cart-form"]') ||
      this.productInfoWrapper.querySelector('form[action*="cart/add"]');

    if (!this.productForm) return;

    this.variantInput = this.productForm.querySelector('input[name="id"]');
    this.sellingPlanInput = this.productForm.querySelector('input[name="selling_plan"]');
    this.submitButton = this.productForm.querySelector('[name="add"]');
    this.submitButtonText = this.submitButton?.querySelector('span');

    this.addToCartLabel =
      (typeof window !== 'undefined' && window.variantStrings?.addToCart) ||
      this.submitButtonText?.textContent?.trim() ||
      'Add to cart';
    this.soldOutLabel =
      (typeof window !== 'undefined' && window.variantStrings?.soldOut) || 'Sold out';
  }

  getOrCreateSellingPlanInput() {
    if (!this.productForm) return null;

    if (!this.sellingPlanInput) {
      this.sellingPlanInput = this.productForm.querySelector('input[name="selling_plan"]');
    }

    if (!this.sellingPlanInput) {
      this.sellingPlanInput = document.createElement('input');
      this.sellingPlanInput.type = 'hidden';
      this.sellingPlanInput.name = 'selling_plan';
      this.sellingPlanInput.value = '';
      this.productForm.appendChild(this.sellingPlanInput);
    }

    return this.sellingPlanInput;
  }

  getDisplayPrice(variant, useSubscription) {
    const regularPrice = Number(variant.price);
    const subscriptionPrice = Number(this.getVariantSellingPlanPrice(variant));

    if (useSubscription && subscriptionPrice) {
      return subscriptionPrice;
    }

    return regularPrice;
  }

  updateProductForm() {
    if (!this.productForm || !this.variantInput) return;

    const variant = this.getSelectedVariant();
    const useSubscription = this.isSubscriptionEnabled();
    const sellingPlanInput = this.getOrCreateSellingPlanInput();

    if (!variant) {
      this.variantInput.value = '';
      if (sellingPlanInput) sellingPlanInput.value = '';
      return;
    }

    this.variantInput.value = variant.id;
    this.variantInput.disabled = !variant.available;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

    if (sellingPlanInput) {
      sellingPlanInput.value = useSubscription ? this.getVariantSellingPlanId(variant) : '';
    }

    this.updateSubmitButton(variant, useSubscription);
  }

  updateSubmitButton(variant, useSubscription) {
    if (!this.submitButton || !this.submitButtonText) return;

    if (!variant.available) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButtonText.textContent = this.soldOutLabel;
      return;
    }

    this.submitButton.removeAttribute('disabled');

    const price = this.getDisplayPrice(variant, useSubscription);
    this.submitButtonText.textContent = `${this.addToCartLabel} | ${this.formatMoney(price)}`;
  }

  parseTexts() {
    const defaults = {
      shippingUnlocked: "You've Unlocked <strong>Free Shipping</strong>",
      shippingUnlock: 'Add [packs] To Unlock Free Shipping',
      shippingPackSingular: '1 More Pack',
      shippingPackPlural: '[count] More Packs',
    };

    const dataEl = this.root.querySelector('[data-nw-purchase-widget-texts]');
    if (!dataEl) return defaults;

    try {
      return { ...defaults, ...JSON.parse(dataEl.textContent) };
    } catch (error) {
      console.warn('NWPurchaseWidget: unable to parse widget text data', error);
      return defaults;
    }
  }

  getShippingPackLabel(packsNeeded) {
    if (packsNeeded === 1) {
      return this.texts.shippingPackSingular;
    }

    return (this.texts.shippingPackPlural || '[count] More Packs').replace(
      '[count]',
      packsNeeded
    );
  }

  buildShippingUnlockMessage(packsNeeded) {
    const packLabel = this.getShippingPackLabel(packsNeeded);
    const packLabelHtml = `<strong class="nw-purchase-widget-shipping-text-highlight">${packLabel}</strong>`;
    const unlockText = this.texts.shippingUnlock || 'Add [packs] To Unlock Free Shipping';

    return unlockText.replace('[packs]', packLabelHtml);
  }

  parseVariants() {
    const dataEl = this.root.querySelector('[data-nw-purchase-widget-variants]');
    if (!dataEl) return [];

    try {
      return JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn('NWPurchaseWidget: unable to parse variant data', error);
      return [];
    }
  }

  normalize(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  formatMonthsLabel(months) {
    if (!months) return '';
    return months === 1 ? '1 Month' : `${months} Months`;
  }

  parseFrequencyMonths(text) {
    if (!text) return null;

    const normalizedText = text.toString();

    const everyMatch = normalizedText.match(/every\s+(\d+)\s*month/i);
    if (everyMatch) return parseInt(everyMatch[1], 10);

    const monthMatches = [...normalizedText.matchAll(/(\d+)\s*month/gi)];
    if (monthMatches.length === 1) {
      return parseInt(monthMatches[0][1], 10);
    }

    if (monthMatches.length > 1) {
      const monthValues = monthMatches.map((match) => parseInt(match[1], 10));
      return Math.max(...monthValues);
    }

    return null;
  }

  getSuggestedRefillMonths(variant) {
    const fallbackMap = {
      'casual roller': [4, 6, 9],
      'dedicated roller': [2, 3, 5],
      'hardcore roller': [1, 2, 3],
    };

    const rollerKey = this.normalize(variant.roller);
    const fallback = fallbackMap[rollerKey];

    if (!fallback || typeof variant.quantityIndex !== 'number') return null;

    return fallback[variant.quantityIndex] ?? null;
  }

  getAllocationMonths(allocation) {
    return (
      this.parseFrequencyMonths(allocation?.frequency) ||
      this.parseFrequencyMonths(allocation?.name)
    );
  }

  getVariantSupplyPlan(variant) {
    const suggestedMonths = this.getSuggestedRefillMonths(variant);
    const allocations = Array.isArray(variant.sellingPlanAllocations)
      ? variant.sellingPlanAllocations
      : [];

    if (allocations.length && suggestedMonths) {
      const matchedAllocation = allocations.find(
        (allocation) => this.getAllocationMonths(allocation) === suggestedMonths
      );

      if (matchedAllocation) return matchedAllocation;
    }

    if (allocations.length) {
      const sortedAllocations = [...allocations].sort(
        (a, b) => (this.getAllocationMonths(a) || 0) - (this.getAllocationMonths(b) || 0)
      );

      if (typeof variant.quantityIndex === 'number' && sortedAllocations[variant.quantityIndex]) {
        return sortedAllocations[variant.quantityIndex];
      }

      return sortedAllocations[0];
    }

    return {
      id: '',
      name: '',
      frequency: this.formatMonthsLabel(suggestedMonths),
      price: '',
    };
  }

  sanitizeFrequencyDisplay(frequency, suggestedMonths, allocationMonths) {
    if (!frequency) return this.formatMonthsLabel(suggestedMonths);

    const trimmed = frequency.toString().trim();

    if (/^\d+\s*month\s*\d+$/i.test(trimmed)) {
      return this.formatMonthsLabel(allocationMonths || suggestedMonths);
    }

    if (/^\d+$/.test(trimmed)) {
      return this.formatMonthsLabel(parseInt(trimmed, 10));
    }

    return trimmed;
  }

  getFrequencyLabel(variant) {
    const suggestedMonths = this.getSuggestedRefillMonths(variant);
    const supplyPlan = this.getVariantSupplyPlan(variant);
    const allocationMonths = this.getAllocationMonths(supplyPlan);

    if (
      supplyPlan.frequency &&
      (!suggestedMonths || allocationMonths === suggestedMonths)
    ) {
      return this.sanitizeFrequencyDisplay(
        supplyPlan.frequency,
        suggestedMonths,
        allocationMonths
      );
    }

    if (allocationMonths && (!suggestedMonths || allocationMonths === suggestedMonths)) {
      return this.formatMonthsLabel(allocationMonths);
    }

    return this.formatMonthsLabel(suggestedMonths);
  }

  getVariantSellingPlanId(variant) {
    return this.getVariantSupplyPlan(variant)?.id || '';
  }

  getVariantSellingPlanPrice(variant) {
    return this.getVariantSupplyPlan(variant)?.price || '';
  }

  hasSupplyFrequency(variant) {
    return Boolean(
      this.getVariantSupplyPlan(variant)?.id ||
        this.getSuggestedRefillMonths(variant) ||
        (variant.sellingPlanAllocations || []).length
    );
  }

  formatMoney(cents) {
    if (cents === null || cents === undefined || cents === '') return '--';

    const amount = Number(cents);
    if (Number.isNaN(amount)) return '--';

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amount, this.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  }

  formatBags(value) {
    if (value === null || value === undefined || value === '') return '--';
    return String(value).padStart(2, '0');
  }

  init() {
    this.applyInitialSelection();

    this.quantityInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (this.isSyncing) return;
        this.onQuantityChange();
      });
    });

    this.rollerInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (this.isSyncing) return;
        this.onRollerChange();
      });
    });

    this.subscriptionToggle?.addEventListener('change', () => {
      this.updateQuantityPrices();
      this.syncSelection();
    });

    this.renderSupplyFrequencies();
    this.updateRollerOptions();
    this.updateSupplyFrequencySelection();
    this.updateQuantityPrices();
    this.syncSelection();
  }

  applyInitialSelection() {
    if (!Number.isNaN(this.defaultQuantityIndex)) {
      const preferredQuantity = Array.from(this.quantityInputs).find(
        (input) => parseInt(input.dataset.quantityIndex, 10) === this.defaultQuantityIndex
      );
      const quantityInput =
        (preferredQuantity && !preferredQuantity.disabled && preferredQuantity) ||
        Array.from(this.quantityInputs).find((input) => !input.disabled);

      if (quantityInput) {
        quantityInput.checked = true;
      }
    }

    if (!this.rollerInputs.length || Number.isNaN(this.defaultRollerIndex)) return;

    this.updateRollerOptions();

    const preferredRoller = Array.from(this.rollerInputs).find(
      (input) => parseInt(input.dataset.rollerIndex, 10) === this.defaultRollerIndex
    );
    const rollerInput =
      (preferredRoller && !preferredRoller.disabled && preferredRoller) ||
      Array.from(this.rollerInputs).find((input) => !input.disabled);

    if (rollerInput) {
      rollerInput.checked = true;
    }
  }

  isSubscriptionEnabled() {
    return this.subscriptionToggle ? this.subscriptionToggle.checked : true;
  }

  updateQuantityPrices() {
    const roller = this.getSelectedRollerValue();
    const useSubscription = this.isSubscriptionEnabled();

    this.quantityInputs.forEach((input) => {
      const variant = this.findVariant(input.value, roller);
      const priceEl = input
        .closest('.nw-purchase-widget-quantity-item')
        ?.querySelector('[data-quantity-price-current]');
      const compareEl = input
        .closest('.nw-purchase-widget-quantity-item')
        ?.querySelector('[data-quantity-price-compare]');

      if (!variant || !priceEl) return;

      const regularPrice = Number(variant.price);
      const subscriptionPrice = Number(this.getVariantSellingPlanPrice(variant));
      const comparePrice = Number(variant.compareAtPrice);
      const displayPrice =
        useSubscription && subscriptionPrice ? subscriptionPrice : regularPrice;

      priceEl.textContent = this.formatMoney(displayPrice);

      input.dataset.sellingPlanId = useSubscription ? this.getVariantSellingPlanId(variant) : '';
      input.dataset.sellingPlanPrice = useSubscription ? subscriptionPrice : '';

      if (compareEl) {
        const showCompare = comparePrice && comparePrice > displayPrice;
        compareEl.textContent = this.formatMoney(comparePrice);
        compareEl.hidden = !showCompare;
      }
    });
  }

  onQuantityChange() {
    this.updateRollerOptions();
    this.updateSupplyFrequencySelection();
    this.updateQuantityPrices();
    this.syncSelection();
  }

  onRollerChange() {
    this.renderSupplyFrequencies();
    this.updateSupplyFrequencySelection();
    this.updateQuantityPrices();
    this.syncSelection();
  }

  onFrequencyChange() {
    if (this.isSyncing) return;

    const selectedFrequency = this.supplyFrequencyContainer?.querySelector(
      'input[name="nw_supply_frequency"]:checked'
    );
    if (!selectedFrequency) return;

    const targetQuantity = selectedFrequency.dataset.quantity;
    if (!targetQuantity) return;

    this.isSyncing = true;

    const quantityInput = Array.from(this.quantityInputs).find(
      (input) => this.normalize(input.value) === this.normalize(targetQuantity)
    );

    if (quantityInput && !quantityInput.disabled) {
      quantityInput.checked = true;
      this.updateRollerOptions();
    }

    this.isSyncing = false;
    this.updateQuantityPrices();
    this.syncSelection();
  }

  getSelectedQuantityValue() {
    return this.root.querySelector('input[name="nw_quantity"]:checked')?.value || null;
  }

  getSelectedRollerValue() {
    return this.root.querySelector('input[name="nw_roller"]:checked')?.value || null;
  }

  findVariant(quantity, roller) {
    if (!quantity || !roller) return null;

    const normalizedQuantity = this.normalize(quantity);
    const normalizedRoller = this.normalize(roller);

    return this.variants.find(
      (variant) =>
        this.normalize(variant.quantity) === normalizedQuantity &&
        this.normalize(variant.roller) === normalizedRoller
    );
  }

  getSelectedVariant() {
    return this.findVariant(this.getSelectedQuantityValue(), this.getSelectedRollerValue());
  }

  getVariantsForRoller(roller) {
    if (!roller) return [];

    const normalizedRoller = this.normalize(roller);

    return this.variants
      .filter((variant) => this.normalize(variant.roller) === normalizedRoller)
      .sort((a, b) => (a.quantityIndex || 0) - (b.quantityIndex || 0));
  }

  renderSupplyFrequencies() {
    if (!this.supplyFrequencyContainer) return;

    const roller = this.getSelectedRollerValue();
    const variants = this.getVariantsForRoller(roller).filter((variant) => this.hasSupplyFrequency(variant));

    this.supplyFrequencyContainer.innerHTML = variants
      .map((variant) => {
        const supplyPlan = this.getVariantSupplyPlan(variant);
        const inputId = `nw-supply-frequency-${variant.id}`;
        const frequencyLabel = this.getFrequencyLabel(variant);

        return `
          <div class="nw-purchase-widget-supply-frequency-item">
            <input
              type="radio"
              name="nw_supply_frequency"
              id="${inputId}"
              value="${supplyPlan.id || variant.id}"
              data-quantity="${variant.quantity}"
              data-variant-id="${variant.id}"
              data-selling-plan-id="${supplyPlan.id || ''}"
            >
            <label for="${inputId}">${frequencyLabel}</label>
          </div>
        `;
      })
      .join('');

    this.supplyFrequencyContainer.querySelectorAll('input[name="nw_supply_frequency"]').forEach((input) => {
      input.addEventListener('change', () => this.onFrequencyChange());
    });
  }

  updateSupplyFrequencySelection() {
    if (!this.supplyFrequencyContainer) return;

    const variant = this.getSelectedVariant();
    const frequencyInputs = this.supplyFrequencyContainer.querySelectorAll(
      'input[name="nw_supply_frequency"]'
    );

    if (!frequencyInputs.length) return;

    let matchedInput = null;

    if (variant) {
      matchedInput = Array.from(frequencyInputs).find(
        (input) => input.dataset.variantId === String(variant.id)
      );
    }

    if (!matchedInput) {
      matchedInput = frequencyInputs[0];
    }

    if (matchedInput) {
      matchedInput.checked = true;
    }
  }

  updateRollerOptions() {
    if (!this.rollerInputs.length) return;

    const quantityValue = this.getSelectedQuantityValue();
    let hasCheckedAvailable = false;

    this.rollerInputs.forEach((input) => {
      const variant = this.findVariant(quantityValue, input.value);
      const subtitleEl = input
        .closest('.nw-purchase-widget-roller-item')
        ?.querySelector('[data-roller-subtitle]');

      if (!variant) {
        input.disabled = true;
        input.dataset.variantId = '';
        return;
      }

      input.disabled = !variant.available;
      input.dataset.variantId = variant.id;
      input.dataset.sellingPlanId = this.getVariantSellingPlanId(variant);
      input.dataset.sellingPlanPrice = this.getVariantSellingPlanPrice(variant);
      input.dataset.freeShipping = variant.freeShipping ? 'true' : 'false';

      if (subtitleEl) {
        subtitleEl.textContent = variant.rollerSubtitle || '';
      }

      if (input.checked && !input.disabled) {
        hasCheckedAvailable = true;
      }
    });

    if (!hasCheckedAvailable) {
      const preferredRoller = Array.from(this.rollerInputs).find(
        (input) =>
          !Number.isNaN(this.defaultRollerIndex) &&
          parseInt(input.dataset.rollerIndex, 10) === this.defaultRollerIndex &&
          !input.disabled
      );
      const fallbackRoller =
        preferredRoller || Array.from(this.rollerInputs).find((input) => !input.disabled);

      if (fallbackRoller) {
        fallbackRoller.checked = true;
        this.renderSupplyFrequencies();
      }
    }
  }

  updateSupplyInfo(variant = this.getSelectedVariant()) {
    if (this.selectedRollerEl) {
      this.selectedRollerEl.textContent = variant?.roller || this.getSelectedRollerValue() || '';
    }

    if (!variant) return;

    if (this.supplyBagsEl) {
      this.supplyBagsEl.textContent = this.formatBags(variant.bagsNeeded);
    }

    if (this.supplyWashesEl) {
      this.supplyWashesEl.textContent =
        variant.totalWashes !== null && variant.totalWashes !== undefined && variant.totalWashes !== ''
          ? variant.totalWashes
          : '--';
    }

    if (this.supplyCostEl) {
      this.supplyCostEl.textContent = this.formatMoney(variant.estimatedCost);
    }
  }

  syncSelection() {
    const variant = this.getSelectedVariant();
    const useSubscription = this.isSubscriptionEnabled();

    if (variant) {
      this.root.dataset.selectedVariantId = variant.id;

      if (useSubscription) {
        this.root.dataset.selectedSellingPlanId = this.getVariantSellingPlanId(variant);
      } else {
        delete this.root.dataset.selectedSellingPlanId;
      }
    } else {
      delete this.root.dataset.selectedVariantId;
      delete this.root.dataset.selectedSellingPlanId;
    }

    this.updateSupplyInfo(variant);
    this.updateShipping(variant);
    this.updateProductForm();
  }

  updateShipping(variant = this.getSelectedVariant()) {
    if (!this.shippingBlock || Number.isNaN(this.unlockIndex)) return;

    const selectedQuantity = this.root.querySelector('input[name="nw_quantity"]:checked');
    if (!selectedQuantity) return;

    const currentIndex = parseInt(selectedQuantity.dataset.quantityIndex, 10);
    const isUnlocked = variant?.freeShipping === true;

    if (isUnlocked) {
      this.shippingBlock.classList.add('is-unlocked');
      this.shippingText.innerHTML =
        this.texts.shippingUnlocked || "You've Unlocked <strong>Free Shipping</strong>";
      this.shippingProgressFill.style.width = '100%';
      return;
    }

    this.shippingBlock.classList.remove('is-unlocked');

    const packsNeeded = this.unlockIndex - currentIndex;
    const progress = ((currentIndex + 1) / (this.unlockIndex + 1)) * 100;

    this.shippingText.innerHTML = this.buildShippingUnlockMessage(packsNeeded);
    this.shippingProgressFill.style.width = `${progress}%`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.nw-purchase-widget').forEach((widget) => {
    new NWPurchaseWidget(widget);
  });
});
